import type {
  RelaxationSession,
  ScheduleRunDetail,
  ScheduleRunSummary,
  ScheduleTaskRunRequest
} from '@/types'
import { http } from '../request'
import {
  isNotFoundError,
  toIsoString,
  toScheduleConflict,
  toScheduleRunSummary
} from './shared'

export const runApi = {
  async startRun(taskId: number, payload?: ScheduleTaskRunRequest): Promise<ScheduleRunSummary> {
    const response = await http.post<ScheduleRunSummary>(`/schedule/tasks/${taskId}/solver-runs`, payload ?? {})
    return toRequiredScheduleRunSummary(response.data)
  },

  async getLatestRelaxationSession(taskId: number): Promise<RelaxationSession | null> {
    try {
      const response = await http.get<RelaxationSession>(`/schedule/tasks/${taskId}/relaxation-sessions/latest`)
      return toRelaxationSession(response.data)
    } catch (error) {
      if (isNotFoundError(error)) {
        return null
      }
      throw error
    }
  },

  async getLatestRun(taskId: number): Promise<ScheduleRunDetail | null> {
    try {
      const response = await http.get<ScheduleRunDetail>(`/schedule/tasks/${taskId}/solver-runs/latest`)
      return toScheduleRunDetail(response.data)
    } catch (error) {
      if (isNotFoundError(error)) {
        return null
      }
      throw error
    }
  },

  async requestSnapshot(taskId: number, runId: number): Promise<ScheduleRunDetail> {
    const response = await http.post<ScheduleRunDetail>(`/schedule/tasks/${taskId}/solver-runs/${runId}/snapshot`)
    return toScheduleRunDetail(response.data)
  },

  async cancelRun(taskId: number, runId: number): Promise<ScheduleRunDetail> {
    const response = await http.post<ScheduleRunDetail>(`/schedule/tasks/${taskId}/solver-runs/${runId}/cancel`)
    return toScheduleRunDetail(response.data)
  },

  async cancelActiveScheduling(taskId: number): Promise<ScheduleRunDetail> {
    const response = await http.post<ScheduleRunDetail>(`/schedule/tasks/${taskId}/solver-runs/cancel-active`)
    return toScheduleRunDetail(response.data)
  },

  /** 确认卡片结果横幅（点 x / 进详情 / 编辑），幂等 */
  async acknowledgeOutcome(taskId: number): Promise<void> {
    await http.post<void>(`/schedule/tasks/${taskId}/outcome-acknowledgement`)
  }
}

function toRequiredScheduleRunSummary(payload: unknown): ScheduleRunSummary {
  const runSummary = toScheduleRunSummary(payload)
  if (!runSummary) {
    throw new Error('排课结果读取失败，请稍后重试')
  }

  return runSummary
}

function toRelaxationSession(payload: unknown): RelaxationSession {
  const session = payload as Record<string, unknown>
  return {
    id: Number(session.id ?? 0),
    taskId: Number(session.taskId ?? 0),
    status: String(session.status ?? ''),
    phase: String(session.phase ?? ''),
    winnerBranch: session.winnerBranch != null ? String(session.winnerBranch) : null,
    winnerRunId: session.winnerRunId != null ? Number(session.winnerRunId) : null,
    fallbackRunId: session.fallbackRunId != null ? Number(session.fallbackRunId) : null,
    stagingCount: session.stagingCount != null ? Number(session.stagingCount) : null,
    verticalStagingCount: session.verticalStagingCount != null ? Number(session.verticalStagingCount) : null,
    horizontalStagingCount: session.horizontalStagingCount != null ? Number(session.horizontalStagingCount) : null,
    teachingPlanVariants: toTeachingPlanVariants(session.teachingPlanVariants),
    stuckObjects: toStuckObjects(session.stuckObjects),
    startedAt: toIsoString(session.startedAt),
    finishedAt: toIsoString(session.finishedAt)
  }
}

function toTeachingPlanVariants(payload: unknown): RelaxationSession['teachingPlanVariants'] {
  if (!payload || typeof payload !== 'object') {
    return null
  }
  const progress = payload as Record<string, unknown>
  return {
    orchestrationVersion: Number(progress.orchestrationVersion ?? 0),
    selectedIndex: String(progress.selectedIndex ?? '0'),
    totalVariants: String(progress.totalVariants ?? '1'),
    attemptedVariants: String(progress.attemptedVariants ?? '0'),
    hasTimedOutCandidate: progress.hasTimedOutCandidate === true,
    hasUncertainCandidate: progress.hasUncertainCandidate === true,
    searchComplete: progress.searchComplete === true,
    allCandidatesAttempted: progress.allCandidatesAttempted === true,
    proofExhausted: progress.proofExhausted === true,
    lastOutcome: progress.lastOutcome != null ? String(progress.lastOutcome) : null,
    strictExtended: progress.strictExtended === true,
    activeRunId: progress.activeRunId != null ? Number(progress.activeRunId) : null,
    lastHandledRunId: progress.lastHandledRunId != null ? Number(progress.lastHandledRunId) : null,
    requestedTimeLimitSeconds: progress.requestedTimeLimitSeconds != null
      ? Number(progress.requestedTimeLimitSeconds)
      : null,
    restartRecoveryCount: Number(progress.restartRecoveryCount ?? 0),
    bestVariantIndex: String(progress.bestVariantIndex ?? '0'),
    bestPlacedActivities: Number(progress.bestPlacedActivities ?? 0),
    currentPlan: progress.currentPlan && typeof progress.currentPlan === 'object'
      ? progress.currentPlan as Record<string, unknown>
      : null
  }
}

function toStuckObjects(payload: unknown): RelaxationSession['stuckObjects'] {
  return Array.isArray(payload)
    ? payload
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        .map((item) => ({
          label: String(item.label ?? ''),
          occurrences: Number(item.occurrences ?? 0)
        }))
    : []
}

function toScheduleRunDetail(payload: unknown): ScheduleRunDetail {
  const run = payload as Record<string, unknown>
  return {
    ...(toScheduleRunSummary(run) ?? {
      id: 0,
      status: '',
      progress: 0,
      snapshotAvailable: false
    }),
    taskId: Number(run.taskId ?? 0),
    exitCode: run.exitCode != null ? Number(run.exitCode) : null,
    workspacePath: toIsoString(run.workspacePath),
    artifactPaths:
      run.artifactPaths && typeof run.artifactPaths === 'object'
        ? Object.fromEntries(
            Object.entries(run.artifactPaths as Record<string, unknown>).map(([key, value]) => [
              key,
              String(value)
            ])
          )
        : {},
    stdout: toIsoString(run.stdout),
    stderr: toIsoString(run.stderr),
    resultLog: toIsoString(run.resultLog),
    warningsLog: toIsoString(run.warningsLog),
    errorsLog: toIsoString(run.errorsLog),
    difficultActivitiesLog: toIsoString(run.difficultActivitiesLog),
    conflicts: Array.isArray(run.conflicts) ? run.conflicts.map((item) => toScheduleConflict(item)) : [],
    snapshotRequestedAt: toIsoString(run.snapshotRequestedAt),
    cancellationRequestedAt: toIsoString(run.cancellationRequestedAt)
  }
}
